import React, { useEffect, useState } from 'react';
import { getDaySchedule, toggleSlot } from '../services/availabilityApi';

/**
 * "+ Add Slot" modal — lets the doctor pick a date and one of the standard
 * time slots and make it available (unblocking it if it was previously blocked).
 * Booked slots can't be selected.
 */
const AddSlotModal = ({ initialDate, onClose, onAdded }) => {
  const [date, setDate] = useState(initialDate);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getDaySchedule(date)
      .then((data) => { if (!cancelled) setSchedule(data); })
      .catch(() => { if (!cancelled) setError('Could not load slots for this date.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [date]);

  const handlePick = async (slot) => {
    if (slot.status !== 'BLOCKED') return; // only need to act on blocked ones; available already open
    setSaving(true);
    setError(null);
    try {
      await toggleSlot(date, slot.time);
      onAdded();
      onClose();
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not open this slot.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-inner" style={{ maxWidth: 420, width: '100%' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">+ Add Slot</h3>
          <p className="modal-sub">Pick a date and an available time to open it up for booking.</p>
        </div>

        <input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          style={{ width: '100%', padding: '9px 10px', border: '1.5px solid #e5e7eb', borderRadius: 8, fontSize: 13, marginBottom: 14 }}
        />

        {error && (
          <div style={{ fontSize: 11.5, color: '#991b1b', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 10px', marginBottom: 12 }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ fontSize: 12.5, color: '#9ca3af' }}>Loading slots…</div>
        ) : !schedule?.workingDay ? (
          <div style={{ fontSize: 12.5, color: '#92400e' }}>This weekday is turned off in your working days — enable it first from the panel.</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
            {schedule.slots.map((slot) => {
              const isBooked = slot.status === 'BOOKED';
              const isBlocked = slot.status === 'BLOCKED';
              return (
                <div
                  key={slot.time}
                  onClick={() => !saving && handlePick(slot)}
                  title={isBooked ? 'Already booked' : isBlocked ? 'Click to open this slot' : 'Already available'}
                  style={{
                    padding: 8, borderRadius: 8, textAlign: 'center', fontSize: 11.5, fontWeight: 600,
                    border: `1.5px solid ${isBooked ? '#bfdbfe' : isBlocked ? '#fca5a5' : '#0e9f6e'}`,
                    background: isBooked ? '#dbeafe' : isBlocked ? '#fee2e2' : '#f0fdf4',
                    color: isBooked ? '#1e40af' : isBlocked ? '#991b1b' : '#065f46',
                    cursor: isBooked ? 'default' : 'pointer',
                    opacity: saving ? 0.6 : 1,
                  }}
                >
                  {slot.label}
                </div>
              );
            })}
          </div>
        )}

        <div className="modal-actions">
          <button className="btn-outline" onClick={onClose} style={{ width: '100%' }}>Close</button>
        </div>
      </div>
    </div>
  );
};

export default AddSlotModal;